import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, 
  Ship, 
  CheckCircle2, 
  XCircle, 
  RefreshCw, 
  Sparkles,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Check,
  X,
  User,
  Award,
  Globe,
  Calendar,
  ArrowRight,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  Edit3,
  Save,
  RotateCcw
} from 'lucide-react';
import type { 
  VesselCrewPlan, 
  PositionSuggestion, 
  CrewSuggestion, 
  CrewMember,
  Vessel
} from '../types';
import { crewApi } from '../services/api';

interface VesselCrewPlanningProps {
  vessel: Vessel;
  vesselCategory: string;
  requiredRanks: string[];
  dueDate?: string;
  onConfirm: (plan: VesselCrewPlan) => void;
  onCancel: () => void;
}

const RANK_NAMES: Record<string, string> = {
  'MSTR': 'Master',
  'COFF': 'Chief Officer',
  '2OFF': 'Second Officer',
  '3OFF': 'Third Officer',
  'CENG': 'Chief Engineer',
  '2ENG': 'Second Engineer',
  '3ENG': 'Third Engineer',
  '4ENG': 'Fourth Engineer',
};

export default function VesselCrewPlanning({
  vessel,
  vesselCategory,
  requiredRanks,
  dueDate,
  onConfirm,
  onCancel
}: VesselCrewPlanningProps) {
  const [crewPlan, setCrewPlan] = useState<VesselCrewPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedPosition, setExpandedPosition] = useState<string | null>(null);
  const [loadingAlternatives, setLoadingAlternatives] = useState<string | null>(null);
  const [modifyingPosition, setModifyingPosition] = useState<string | null>(null);
  
  useEffect(() => {
    generateCrewPlan();
  }, [vessel.id, requiredRanks]);

  const generateCrewPlan = async () => {
    setIsLoading(true);
    setIsGenerating(true);
    setError(null);
    
    try {
      const plan = await crewApi.suggestCrewPlan({
        vessel_id: vessel.id,
        vessel_name: vessel.name,
        vessel_category: vesselCategory,
        required_ranks: requiredRanks,
        due_date: dueDate
      });
      setCrewPlan(plan);
    } catch (err: any) {
      console.error('Error generating crew plan:', err);
      setError(err.response?.data?.detail || 'Failed to generate crew plan');
    } finally {
      setIsLoading(false);
      setIsGenerating(false);
    }
  };

  const handleAcceptSuggestion = (positionRank: string) => {
    if (!crewPlan) return;
    
    setCrewPlan({
      ...crewPlan,
      positions: crewPlan.positions.map(pos => 
        pos.position === positionRank
          ? { ...pos, status: 'accepted', selected_crew: pos.suggested_crew }
          : pos
      )
    });
  };

  const handleRejectSuggestion = (positionRank: string) => {
    if (!crewPlan) return;
    
    setCrewPlan({
      ...crewPlan,
      positions: crewPlan.positions.map(pos => 
        pos.position === positionRank
          ? { ...pos, status: 'rejected', selected_crew: null }
          : pos
      )
    });
    setExpandedPosition(positionRank);
  };

  const handleSelectAlternative = (positionRank: string, crew: CrewSuggestion) => {
    if (!crewPlan) return;
    
    setCrewPlan({
      ...crewPlan,
      positions: crewPlan.positions.map(pos => 
        pos.position === positionRank
          ? { ...pos, status: 'changed', selected_crew: crew }
          : pos
      )
    });
    setExpandedPosition(null);
    setModifyingPosition(null);
  };

  const loadAlternatives = async (positionRank: string) => {
    setLoadingAlternatives(positionRank);
    setExpandedPosition(positionRank);
    
    try {
      const candidates = await crewApi.getCandidatesForRank(
        vessel.id, 
        positionRank, 
        vesselCategory
      );
      
      if (crewPlan) {
        setCrewPlan({
          ...crewPlan,
          positions: crewPlan.positions.map(pos => 
            pos.position === positionRank
              ? { 
                  ...pos, 
                  alternatives: candidates.map(c => ({
                    cid: c.cid,
                    name: `${c.name || ''} ${c.surname || ''}`.trim(),
                    nationality: c.nationality,
                    due_date: c.due_date || c.due_soff_date,
                    vessel_type: c.vessel_type || c.crew_type,
                    match_score: c.match_score || 80,
                    match_reasons: c.match_reasons
                  }))
                }
              : pos
          )
        });
      }
    } catch (err) {
      console.error('Error loading alternatives:', err);
    } finally {
      setLoadingAlternatives(null);
    }
  };

  const handleConfirmPlan = () => {
    if (!crewPlan) return;
    onConfirm(crewPlan);
  };

  const acceptedCount = crewPlan?.positions.filter(p => 
    p.status === 'accepted' || p.status === 'changed'
  ).length || 0;
  
  const totalPositions = crewPlan?.positions.length || 0;

  const canConfirm = acceptedCount > 0;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          className="mb-4"
        >
          <Sparkles className="w-16 h-16 text-blue-500" />
        </motion.div>
        <h3 className="text-lg font-medium text-slate-800 mb-2">
          AI is analyzing crew availability...
        </h3>
        <p className="text-slate-500">
          Finding the best matches for {vessel.name}
        </p>
        <div className="mt-4 flex gap-2">
          {requiredRanks.map((rank, index) => (
            <motion.span
              key={rank}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-sm"
            >
              {RANK_NAMES[rank] || rank}
            </motion.span>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-800 mb-2">
          Unable to Generate Crew Plan
        </h3>
        <p className="text-slate-500 mb-6">{error}</p>
        <button
          onClick={generateCrewPlan}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <Ship className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-800">{vessel.name}</h2>
            <p className="text-sm text-slate-500">
              {vesselCategory} • {totalPositions} positions to fill
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={generateCrewPlan}
            disabled={isGenerating}
            className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2 text-slate-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            <span className="font-medium text-slate-800">AI Crew Suggestions</span>
          </div>
          <span className="text-sm text-slate-600">
            {acceptedCount} of {totalPositions} positions confirmed
          </span>
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${(acceptedCount / totalPositions) * 100}%` }}
            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500"
          />
        </div>
      </div>

      {/* Position Cards */}
      <div className="space-y-4">
        <AnimatePresence mode="popLayout">
          {crewPlan?.positions.map((position, index) => (
            <PositionCard
              key={position.position}
              position={position}
              index={index}
              isExpanded={expandedPosition === position.position}
              isModifying={modifyingPosition === position.position}
              loadingAlternatives={loadingAlternatives === position.position}
              onAccept={() => handleAcceptSuggestion(position.position)}
              onReject={() => handleRejectSuggestion(position.position)}
              onExpand={() => {
                if (expandedPosition === position.position) {
                  setExpandedPosition(null);
                } else {
                  loadAlternatives(position.position);
                }
              }}
              onModify={() => {
                setModifyingPosition(position.position);
                loadAlternatives(position.position);
              }}
              onSelectAlternative={(crew) => handleSelectAlternative(position.position, crew)}
              onReset={() => {
                if (crewPlan) {
                  setCrewPlan({
                    ...crewPlan,
                    positions: crewPlan.positions.map(pos => 
                      pos.position === position.position
                        ? { ...pos, status: 'suggested', selected_crew: null }
                        : pos
                    )
                  });
                }
              }}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pt-6 border-t border-slate-200">
        <button
          onClick={onCancel}
          className="px-6 py-3 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors text-slate-700"
        >
          Cancel
        </button>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-500">
            {acceptedCount > 0 ? (
              <span className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                {acceptedCount} crew member{acceptedCount !== 1 ? 's' : ''} selected
              </span>
            ) : (
              <span>Review and accept suggestions to continue</span>
            )}
          </div>
          
          <button
            onClick={handleConfirmPlan}
            disabled={!canConfirm}
            className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            <Check className="w-5 h-5" />
            Confirm Selections ({acceptedCount})
          </button>
        </div>
      </div>
    </div>
  );
}

interface PositionCardProps {
  position: PositionSuggestion;
  index: number;
  isExpanded: boolean;
  isModifying: boolean;
  loadingAlternatives: boolean;
  onAccept: () => void;
  onReject: () => void;
  onExpand: () => void;
  onModify: () => void;
  onSelectAlternative: (crew: CrewSuggestion) => void;
  onReset: () => void;
}

function PositionCard({
  position,
  index,
  isExpanded,
  isModifying,
  loadingAlternatives,
  onAccept,
  onReject,
  onExpand,
  onModify,
  onSelectAlternative,
  onReset
}: PositionCardProps) {
  const getStatusColor = () => {
    switch (position.status) {
      case 'accepted':
      case 'changed':
        return 'border-green-300 bg-green-50';
      case 'rejected':
        return 'border-red-300 bg-red-50';
      case 'suggested':
        return 'border-blue-300 bg-blue-50';
      default:
        return 'border-slate-200 bg-white';
    }
  };

  const getStatusBadge = () => {
    switch (position.status) {
      case 'accepted':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle2 className="w-3 h-3" /> Accepted
          </span>
        );
      case 'changed':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
            <Edit3 className="w-3 h-3" /> Changed
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" /> Rejected
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
            <Sparkles className="w-3 h-3" /> Suggested
          </span>
        );
    }
  };

  const displayCrew = position.selected_crew || position.suggested_crew;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`rounded-xl border-2 overflow-hidden transition-all ${getStatusColor()}`}
    >
      {/* Main Row */}
      <div className="p-4">
        <div className="flex items-center justify-between">
          {/* Position Info */}
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center text-white font-bold text-sm">
              {position.position}
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">
                {position.position_name || RANK_NAMES[position.position] || position.position}
              </h3>
              {getStatusBadge()}
            </div>
          </div>

          {/* Suggested/Selected Crew */}
          {displayCrew ? (
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium text-slate-800">{displayCrew.name}</span>
                  <span className="text-sm text-slate-500">({displayCrew.cid})</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                  {displayCrew.nationality && (
                    <span className="flex items-center gap-1">
                      <Globe className="w-3 h-3" />
                      {displayCrew.nationality}
                    </span>
                  )}
                  {displayCrew.vessel_type && (
                    <span className="flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      {displayCrew.vessel_type} experience
                    </span>
                  )}
                </div>
              </div>

              {/* Match Score */}
              <div className={`w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg ${
                displayCrew.match_score >= 90 ? 'bg-green-100 text-green-700' :
                displayCrew.match_score >= 70 ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {displayCrew.match_score}%
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2">
                {position.status === 'suggested' ? (
                  <>
                    <button
                      onClick={onAccept}
                      className="p-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                      title="Accept"
                    >
                      <ThumbsUp className="w-5 h-5" />
                    </button>
                    <button
                      onClick={onReject}
                      className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                      title="Reject"
                    >
                      <ThumbsDown className="w-5 h-5" />
                    </button>
                    <button
                      onClick={onModify}
                      className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                      title="Change"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={onReset}
                      className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                      title="Reset"
                    >
                      <RotateCcw className="w-5 h-5" />
                    </button>
                    <button
                      onClick={onModify}
                      className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                      title="Change"
                    >
                      <Edit3 className="w-5 h-5" />
                    </button>
                  </>
                )}
                <button
                  onClick={onExpand}
                  className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                  title={isExpanded ? 'Hide alternatives' : 'Show alternatives'}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-5 h-5" />
                  ) : (
                    <ChevronRight className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <span className="text-slate-500 italic">No crew suggested</span>
              <button
                onClick={onExpand}
                className="px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-colors flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                Find Crew
              </button>
            </div>
          )}
        </div>

        {/* Match Reasons */}
        {displayCrew?.match_reasons && displayCrew.match_reasons.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 pl-16">
            {displayCrew.match_reasons.map((reason, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-white/60 text-slate-600 rounded-md text-xs"
              >
                {reason}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Alternatives Panel */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-slate-200 bg-white"
          >
            <div className="p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-3">
                Alternative Candidates
              </h4>
              
              {loadingAlternatives ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                  <span className="ml-2 text-slate-500">Loading alternatives...</span>
                </div>
              ) : position.alternatives.length > 0 ? (
                <div className="grid gap-2">
                  {position.alternatives.map((alt) => (
                    <div
                      key={alt.cid}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{alt.name}</div>
                          <div className="flex items-center gap-3 text-sm text-slate-500">
                            <span>{alt.cid}</span>
                            {alt.nationality && <span>{alt.nationality}</span>}
                            {alt.vessel_type && <span>{alt.vessel_type}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          alt.match_score >= 90 ? 'bg-green-100 text-green-700' :
                          alt.match_score >= 70 ? 'bg-blue-100 text-blue-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {alt.match_score}%
                        </div>
                        <button
                          onClick={() => onSelectAlternative(alt)}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors opacity-0 group-hover:opacity-100 flex items-center gap-2"
                        >
                          <Check className="w-4 h-4" />
                          Select
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-500">
                  No alternative candidates available for this position
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

